'use client';

import {
	useRef,
	useState,
	Children,
	isValidElement,
	type HTMLAttributes,
	type ReactNode,
	type KeyboardEventHandler,
	type UIEventHandler,
	type ElementType,
} from 'react';
import cn from 'classnames';
import Arrow from 'assets/icons/arrow.svg';
import { useDraggable } from 'react-use-draggable-scroll';
import { focusableElements } from 'helpers/helpers';
import useMediaQuery from 'hooks/use-media-query';
import { BREAKPOINT_M } from 'lib/constants.mjs';
import styles from './item-collection-container.module.scss';

const SHELF_BUFFER_PX = 40; // A pixel value that represents a buffer for figuring out start/end position.

const ItemCollectionContainer = ({
	type,
	as: Element = 'div',
	gap = 'var(--spacing-xxs)',
	shelfOptions: {
		isRightToLeft = false,
		autoSizeItems = false,
		autoSizedVisibleItemCount = 2,
		hasClampedWidth: hasClampedShelfWidth = false,
	} = {},
	gridOptions: {
		columnWidth = null, // columnWidth overrides column count
		columns = 3,
		hasClampedWidth: hasClampedGridWidth = true,
	} = {},
	className,
	children,
	...restProps
}: ItemCollectionContainerProps) => {
	const [showNextButton, setShowNextButton] = useState(false);
	const [showPreviousButton, setShowPreviousButton] = useState(false);

	const items = Children.toArray(children);
	const itemsContainerRef = useRef<HTMLDivElement>(null);
	const { events } = useDraggable(itemsContainerRef, {
		isMounted: items.length > 0,
	});
	const { matches: isDesktop } = useMediaQuery(
		`(min-width: ${BREAKPOINT_M}px)`,
	);
	const mode = getModeFromType(type, isDesktop);

	const baseCustomProperties = getGapCustomProperties(gap);
	const shelfCustomProperties =
		getShelfAutoSizedVisibleItemCountCustomProperties(
			autoSizedVisibleItemCount,
		);
	const gridCustomProperties = {
		'--grid-columns': columns,
		'--grid-column-width': columnWidth,
	};
	const hasGridStyles = type === 'grid' || type === 'grelf';
	const hasShelfStyles = type === 'shelf' || type === 'grelf';

	const handleKeyDown: KeyboardEventHandler<HTMLDivElement> = (e) => {
		if (e.key !== 'ArrowDown') {
			return;
		}

		const lastChild = itemsContainerRef.current.lastElementChild;

		//For non-focuable slides that contain links, inputs, etc...
		const lastFocusableItemInLastchild = [
			...lastChild.querySelectorAll<HTMLInputElement>(focusableElements),
		].at(-1);

		//For focusable (<a/>, <input/>, <button>) slides.
		const lastFocusableItem = [
			...itemsContainerRef.current.querySelectorAll<HTMLInputElement>(
				focusableElements,
			),
		].at(-1);

		(lastFocusableItemInLastchild || lastFocusableItem)?.focus();
	};
	const handleScrollTrigger = (forward = true) => {
		if (mode === 'grid') {
			return;
		}

		const newPosition = forward
			? itemsContainerRef.current.scrollLeft +
				itemsContainerRef.current?.offsetWidth
			: itemsContainerRef.current?.scrollLeft -
				itemsContainerRef.current?.offsetWidth;

		itemsContainerRef.current.scrollTo({
			left: newPosition,
			behavior: 'smooth',
		});
	};
	const handleBackArrowClick = () => handleScrollTrigger(false);
	const handleForwardArrowClick = () => handleScrollTrigger();
	const handleShowingArrows: UIEventHandler<HTMLDivElement> = () => {
		if (mode === 'grid') {
			return;
		}

		const sliderRef = itemsContainerRef.current;

		if (isRightToLeft) {
			const absoluteLeft = Math.abs(sliderRef?.scrollLeft);
			setShowNextButton(absoluteLeft > SHELF_BUFFER_PX);
			setShowPreviousButton(
				absoluteLeft + sliderRef.offsetWidth <
					sliderRef.scrollWidth - SHELF_BUFFER_PX,
			);
			return;
		}

		// If there is less than the specified buffer pixels to move left - hide the "Prev" Arrow.
		setShowPreviousButton(sliderRef?.scrollLeft > SHELF_BUFFER_PX);
		//If there is less than the specified buffer pixels to the right - hide the "Next"
		setShowNextButton(
			sliderRef?.scrollLeft + sliderRef.offsetWidth <
				sliderRef.scrollWidth - SHELF_BUFFER_PX,
		);
	};

	if (!items?.length) {
		return null;
	}

	return (
		<Element
			className={cn(styles.itemCollection, styles[type], className)}
			style={{
				...baseCustomProperties,
				...(hasShelfStyles ? shelfCustomProperties : {}),
				...(hasGridStyles ? gridCustomProperties : {}),
			}}
			onKeyDown={handleKeyDown}
			onMouseOver={handleShowingArrows}
			onScroll={handleShowingArrows}
			data-testid="item-collection-container"
			{...restProps}
		>
			<div
				tabIndex={mode === 'shelf' ? 0 : -1}
				className={cn(
					styles.itemsContainer,
					hasGridStyles && {
						[styles.gridByColumnWidth]: columnWidth,
						[styles.gridByColumnCount]: !columnWidth,
						[styles.gridClampedWidth]: hasClampedGridWidth,
						[styles.gridEvenColumns]: items.length % 2 === 0,
					},
					hasShelfStyles && {
						[styles.shelfClampedWidth]: hasClampedShelfWidth,
					},
				)}
				{...events}
				ref={itemsContainerRef}
			>
				{autoSizeItems
					? items.map((child, i) => (
							<div
								key={isValidElement(child) ? child.key : `item-${i}`}
								className={styles.autoSizedItem}
							>
								{child}
							</div>
						))
					: items}
			</div>

			{showPreviousButton && (
				<button
					aria-label="Previous button"
					onClick={handleBackArrowClick}
					className={cn(styles.button, styles.backButton)}
				>
					<Arrow />
				</button>
			)}

			{showNextButton && (
				<button
					aria-label="Next button"
					onClick={handleForwardArrowClick}
					className={cn(styles.button, styles.forwardButton)}
				>
					<Arrow />
				</button>
			)}
		</Element>
	);
};

// We don't set an initial value because the shelves always collapse
// to a single column below breakpoint-s.
function getShelfAutoSizedVisibleItemCountCustomProperties(
	value: AutoSizedVisibleItemCount,
) {
	const normalizedVal =
		typeof value === 'number'
			? { breakpointS: value, breakpointM: value }
			: value;

	return {
		'--shelf-auto-sized-visible-item-count-s': normalizedVal.breakpointS,
		'--shelf-auto-sized-visible-item-count-m': normalizedVal.breakpointM,
	};
}

// Since we practice mobile-first design, Each breakpoint falls back to the
// last one that was defined. This allows the consumer to skip any intermediate
// breakpoints that should inherit the value from the smaller breakpoint.
function getGapCustomProperties(value: Gap) {
	const {
		initial,
		breakpointS = initial,
		breakpointM = breakpointS,
	}: GapByBreakpoint = typeof value === 'string' ? { initial: value } : value;

	return {
		'--collection-gap': initial,
		'--collection-gap-s': breakpointS,
		'--collection-gap-m': breakpointM,
	};
}

function getModeFromType(type: 'grid' | 'shelf' | 'grelf', isDesktop: boolean) {
	if (isDesktop) {
		return type === 'shelf' ? 'shelf' : 'grid';
	} else {
		return type === 'grid' ? 'grid' : 'shelf';
	}
}

export interface ItemCollectionContainerProps extends Omit<
	HTMLAttributes<HTMLElement>,
	'style' | 'onKeyDown' | 'onMouseOver' | 'onScroll'
> {
	// 'grelf' is short for 'grid shelf', which is a shelf on mobile
	// and a grid on desktop.
	type: 'grid' | 'shelf' | 'grelf';
	as?: ElementType;
	gap?: Gap;
	gridOptions?: {
		columnWidth?: string;
		columns?: number;
		hasClampedWidth?: boolean;
	};
	shelfOptions?: {
		isRightToLeft?: boolean;
		autoSizeItems?: boolean;
		hasClampedWidth?: boolean;
		autoSizedVisibleItemCount?: AutoSizedVisibleItemCount;
	};
	children?: ReactNode;
}
type AutoSizedVisibleItemCount =
	number | { breakpointS: number; breakpointM: number };
type GapByBreakpoint = {
	initial: string;
	breakpointS?: string;
	breakpointM?: string;
};
type Gap = string | GapByBreakpoint;

export default ItemCollectionContainer;
